import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';

const computePositionsFromRounds = (rounds) => {
  const userStats = {};
  rounds.forEach(round => {
    const userId = round.userId;
    if (!userStats[userId]) {
      userStats[userId] = { userId, totalPoints: 0, roundsPlayed: 0, allScores: [] };
    }
    userStats[userId].totalPoints += round.totalStableford || 0;
    userStats[userId].roundsPlayed += 1;
    userStats[userId].allScores.push(round.totalStableford || 0);
  });
  const lb = Object.values(userStats).map(stats => {
    let dropCount = 0;
    if (stats.roundsPlayed >= 25) dropCount = 4;
    else if (stats.roundsPlayed >= 20) dropCount = 3;
    else if (stats.roundsPlayed >= 15) dropCount = 2;
    else if (stats.roundsPlayed >= 10) dropCount = 1;
    let adjustedTotal = stats.totalPoints;
    let countingRounds = stats.roundsPlayed;
    if (dropCount > 0) {
      const sorted = [...stats.allScores].sort((a, b) => a - b);
      adjustedTotal -= sorted.slice(0, dropCount).reduce((s, v) => s + v, 0);
      countingRounds -= dropCount;
    }
    return {
      userId: stats.userId,
      averagePoints: parseFloat(countingRounds > 0 ? (adjustedTotal / countingRounds).toFixed(2) : '0.00')
    };
  });
  lb.sort((a, b) => b.averagePoints - a.averagePoints);
  const positions = {};
  lb.forEach((p, i) => { positions[p.userId] = i + 1; });
  return positions;
};

const TEEBOX_COLORS = {
  wit: '#ffffff',
  geel: '#d4af37',
  blauw: '#3b82f6',
  rood: '#ef4444',
  oranje: '#f97316',
};

function Leaderboard({ onClose, onViewProfile, hallOfFame = [] }) {
  const [view, setView] = useState('season'); // season, event, finale
  const [activeSeason, setActiveSeason] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [seasonLeaderboard, setSeasonLeaderboard] = useState([]);
  const [eventLeaderboard, setEventLeaderboard] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [positionChanges, setPositionChanges] = useState({});
  const [finaleEvents, setFinaleEvents] = useState([]);
  const [selectedFinaleEvent, setSelectedFinaleEvent] = useState(null);
  const [finaleLeaderboard, setFinaleLeaderboard] = useState([]);
  const [finaleLoading, setFinaleLoading] = useState(false);

  // Load active season
  useEffect(() => {
    const loadActiveSeason = async () => {
      try {
        const seasonsQuery = query(collection(db, 'seasons'), where('isActive', '==', true));
        const seasonsSnapshot = await getDocs(seasonsQuery);
        
        if (!seasonsSnapshot.empty) {
          const season = { id: seasonsSnapshot.docs[0].id, ...seasonsSnapshot.docs[0].data() };
          setActiveSeason(season);
        }
      } catch (error) {
        console.error('Error loading active season:', error);
      }
    };

    loadActiveSeason();
  }, []);

  // Load events for active season
  useEffect(() => {
    if (!activeSeason) return;

    const eventsQuery = query(
      collection(db, 'events'),
      where('seasonId', '==', activeSeason.id)
    );

    const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
      const eventsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setEvents(eventsData);

      // Auto-select most recent event
      if (eventsData.length > 0 && !selectedEvent) {
        setSelectedEvent(eventsData[0]);
      }

      const finaleEventsData = eventsData.filter(e => e.type === 'finale_18');
      setFinaleEvents(finaleEventsData);
      if (finaleEventsData.length > 0 && !selectedFinaleEvent) {
        setSelectedFinaleEvent(finaleEventsData[0]);
      }
    });

    return unsubscribe;
  }, [activeSeason]);

  // Load all users
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const usersMap = {};
      snapshot.docs.forEach(doc => {
        usersMap[doc.id] = doc.data();
      });
      setUsers(usersMap);
    });

    return unsubscribe;
  }, []);

  // Load season leaderboard
  useEffect(() => {
    if (!activeSeason) return;

    const loadSeasonLeaderboard = async () => {
      try {
        setLoading(true);
        
        const roundsQuery = query(
          collection(db, 'rounds'),
          where('seasonId', '==', activeSeason.id),
          where('status', '==', 'approved')
        );

        const roundsSnapshot = await getDocs(roundsQuery);
        const seasonRounds = roundsSnapshot.docs.map(d => d.data()).filter(r => !r.isFinale);

        // Group by user
        const userStats = {};

        seasonRounds.forEach(round => {
          const userId = round.userId;
          
          if (!userStats[userId]) {
            userStats[userId] = {
              userId: userId,
              totalPoints: 0,
              roundsPlayed: 0,
              bestRound: 0,
              totalGross: 0,
              rounds: []
            };
          }
          
          userStats[userId].totalPoints += round.totalStableford;
          userStats[userId].totalGross += round.totalGross;
          userStats[userId].roundsPlayed += 1;
          userStats[userId].rounds.push(round.totalStableford);
          
          if (round.totalStableford > userStats[userId].bestRound) {
            userStats[userId].bestRound = round.totalStableford;
          }
        });
        
        // Convert to array and calculate averages
        const leaderboard = Object.values(userStats).map(stats => ({
          ...stats,
          averagePoints: (stats.totalPoints / stats.roundsPlayed).toFixed(2),
          averageGross: Math.round(stats.totalGross / stats.roundsPlayed)
        }));

        // Sort by average points (descending) - rewards consistency
        leaderboard.sort((a, b) => parseFloat(b.averagePoints) - parseFloat(a.averagePoints));
        
        setSeasonLeaderboard(leaderboard);

        // Compute position changes by comparing current snapshot vs previous snapshot
        // (all rounds except those approved on the most recent approval date)
        const allRoundsData = seasonRounds;
        const approvalDates = allRoundsData
          .map(r => r.approvedAt ? r.approvedAt.split('T')[0] : null)
          .filter(Boolean);
        const mostRecentDate = approvalDates.length > 0
          ? [...approvalDates].sort().pop()
          : null;

        const previousRounds = mostRecentDate
          ? allRoundsData.filter(r =>
              !r.approvedAt || r.approvedAt.split('T')[0] !== mostRecentDate
            )
          : [];

        const previousPositions = previousRounds.length > 0
          ? computePositionsFromRounds(previousRounds)
          : {};

        const changes = {};
        leaderboard.forEach((player, index) => {
          const currentPos = index + 1;
          const prevPos = previousPositions[player.userId];
          changes[player.userId] = prevPos !== undefined ? prevPos - currentPos : 'new';
        });
        setPositionChanges(changes);
      } catch (error) {
        console.error('Error loading season leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSeasonLeaderboard();
  }, [activeSeason]);

  // Load event leaderboard
  useEffect(() => {
    if (!selectedEvent) return;

    const loadEventLeaderboard = async () => {
      try {
        setLoading(true);
        
        const roundsQuery = query(
          collection(db, 'rounds'),
          where('eventId', '==', selectedEvent.id),
          where('status', '==', 'approved')
        );

        const roundsSnapshot = await getDocs(roundsQuery);
        
        const leaderboard = roundsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        
        // Sort by appropriate metric
        if (selectedEvent.type === 'finale') {
          // Stroke play - lowest gross score wins
          leaderboard.sort((a, b) => a.totalGross - b.totalGross);
        } else {
          // Stableford - highest points wins
          leaderboard.sort((a, b) => b.totalStableford - a.totalStableford);
        }
        
        setEventLeaderboard(leaderboard);
      } catch (error) {
        console.error('Error loading event leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEventLeaderboard();
  }, [selectedEvent]);

  // Load finale leaderboard
  useEffect(() => {
    if (!activeSeason) return;

    const loadFinaleLeaderboard = async () => {
      try {
        setFinaleLoading(true);

        const roundsQuery = query(
          collection(db, 'rounds'),
          where('seasonId', '==', activeSeason.id),
          where('isFinale', '==', true)
        );

        const roundsSnapshot = await getDocs(roundsQuery);

        const leaderboard = roundsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));

        leaderboard.sort((a, b) => a.finaleNetGross - b.finaleNetGross);

        setFinaleLeaderboard(leaderboard);
      } catch (error) {
        console.error('Error loading finale leaderboard:', error);
      } finally {
        setFinaleLoading(false);
      }
    };

    loadFinaleLeaderboard();
  }, [activeSeason]);

  const getUserName = (userId) => {
    return users[userId]?.name || 'Unknown';
  };

  const seasonChampionIds = new Set(hallOfFame.filter(h => h.type === 'season_champion').map(h => h.userId));

  const getMedalEmoji = (position) => {
    if (position === 0) return '🥇';
    if (position === 1) return '🥈';
    if (position === 2) return '🥉';
    return '';
  };

  return (
    <div style={styles.overlay}>
      <style>{`
        @media (max-width: 480px) {
          .leaderboard-content { padding: 12px !important; }
          .season-table-header,
          .season-table-row {
            grid-template-columns: 36px minmax(0, 1fr) 52px 24px 28px !important;
            gap: 4px !important;
            padding: 10px 8px !important;
          }
          .stat-value { font-size: 13px !important; }
          .season-table-row .position-cell { font-size: 12px !important; }
        }
      `}</style>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>🏆 Leaderboards</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {!activeSeason ? (
          <div style={styles.emptyState}>
            <p>No active season found</p>
          </div>
        ) : (
          <>
            <div style={styles.seasonBanner}>
              <span style={styles.seasonName}>{activeSeason.name}</span>
              <span style={styles.seasonDates}>
                {new Date(activeSeason.startDate).toLocaleDateString()} - {new Date(activeSeason.endDate).toLocaleDateString()}
              </span>
            </div>

            <div style={styles.tabs}>
              <button
                onClick={() => setView('season')}
                style={view === 'season' ? styles.tabActive : styles.tab}
              >
                Season Standings
              </button>
              <button
                onClick={() => setView('event')}
                style={view === 'event' ? styles.tabActive : styles.tab}
              >
                Event Results
              </button>
              {finaleEvents.length > 0 && (
                <button
                  onClick={() => setView('finale')}
                  style={view === 'finale' ? styles.tabActive : styles.tab}
                >
                  🏆 Finale
                </button>
              )}
            </div>

            <div style={styles.content} className="leaderboard-content">
  {view === 'season' && (
    <div>
      <h3 style={styles.sectionTitle}>Overall Season Rankings</h3>
      <p style={styles.subtitle}>Based on average Stableford points per round</p>

      {loading ? (
        <div style={styles.loadingState}>Loading...</div>
      ) : seasonLeaderboard.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No approved rounds yet this season</p>
        </div>
      ) : (
        <div style={styles.leaderboardTable}>
          <div style={{...styles.tableHeader, gridTemplateColumns: '60px 1fr 80px 44px 50px'}} className="season-table-header">
            <div style={styles.positionHeader}>#</div>
            <div style={styles.nameHeader}>Player</div>
            <div style={styles.statHeader}>Avg</div>
            <div style={styles.statHeader}>R</div>
            <div style={styles.statHeader}>Δ</div>
          </div>
          {seasonLeaderboard.map((player, index) => {
            const isChampion = seasonChampionIds.has(player.userId);
            const diff = positionChanges[player.userId];
            const changeColor = diff > 0 ? '#10b981' : diff < 0 ? '#ef4444' : '#6b7280';
            const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '-';
            return (
            <div key={player.userId} style={{
              ...styles.tableRow,
              gridTemplateColumns: '60px 1fr 80px 44px 50px',
              ...(isChampion ? { borderLeft: '2px solid rgba(212,175,55,0.55)', backgroundColor: 'rgba(212,175,55,0.05)' } : {})
            }} className="season-table-row">
              <div style={styles.position} className="position-cell">
                {getMedalEmoji(index)} {index + 1}
              </div>
              <div
                style={{
                  ...styles.playerName,
                  ...(onViewProfile ? styles.playerNameLink : {}),
                  ...(isChampion ? { color: '#d4af37' } : {})
                }}
                onClick={onViewProfile ? () => onViewProfile(player.userId) : undefined}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  minWidth: 0,
                  overflow: 'hidden',
                  width: '100%',
                }}>
                  {isChampion && (
                    <span style={{
                      fontSize: '11px',
                      flexShrink: 0,
                      lineHeight: '1.4',
                      marginTop: '1px'
                    }}>👑</span>
                  )}
                  <span style={{
                    lineHeight: '1.4',
                    display: 'block',
                    width: '100%',
                    minWidth: 0,
                    wordBreak: 'keep-all',
                    overflowWrap: 'break-word',
                  }}>
                    {getUserName(player.userId)}
                  </span>
                </div>
              </div>
              <div style={styles.statValue} className="stat-value">{parseFloat(player.averagePoints).toFixed(2)}</div>
              <div style={styles.statValue}>{player.roundsPlayed}</div>
              <div style={styles.changeCell}>
                <span style={{color: changeColor}}>{arrow}</span>
                {diff !== 0 && diff !== 'new' && diff !== undefined && (
                  <span style={{color: changeColor}}>{Math.abs(diff)}</span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  )}

              {view === 'event' && (
                <div>
                  <h3 style={styles.sectionTitle}>Event Results</h3>
                  
                  {events.length === 0 ? (
                    <div style={styles.emptyState}>
                      <p>No events in this season yet</p>
                    </div>
                  ) : (
                    <>
                      <div style={styles.eventSelector}>
                        <label style={styles.label}>Select Event:</label>
                        <select
                          value={selectedEvent?.id || ''}
                          onChange={(e) => {
                            const event = events.find(ev => ev.id === e.target.value);
                            setSelectedEvent(event);
                          }}
                          style={styles.select}
                        >
                          {events.map(event => (
                            <option key={event.id} value={event.id}>
                              {event.name} - {new Date(event.date).toLocaleDateString()}
                              {event.type === 'finale' ? ' (FINALE)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedEvent && (
                        <>
                          <div style={styles.eventInfo}>
                            <div style={styles.eventDetail}>
                              <span style={styles.eventLabel}>Format:</span>
                              <span style={styles.eventValue}>
                                {selectedEvent.type === 'finale' ? '⭐ Stroke Play (Finale)' : 'Stableford'}
                              </span>
                            </div>
                            <div style={styles.eventDetail}>
                              <span style={styles.eventLabel}>Date:</span>
                              <span style={styles.eventValue}>
                                {new Date(selectedEvent.date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>

                          {loading ? (
                            <div style={styles.loadingState}>Loading...</div>
                          ) : eventLeaderboard.length === 0 ? (
                            <div style={styles.emptyState}>
                              <p>No approved rounds for this event yet</p>
                            </div>
                          ) : (
                            <div style={styles.leaderboardTable}>
                              <div style={styles.tableHeader}>
                                <div style={styles.positionHeader}>#</div>
                                <div style={styles.nameHeader}>Player</div>
                                <div style={styles.statHeader}>Course</div>
                                {selectedEvent.type === 'finale' ? (
                                  <>
                                    <div style={styles.statHeader}>Gross</div>
                                    <div style={styles.statHeader}>HCP</div>
                                    <div style={styles.statHeader}>Net</div>
                                  </>
                                ) : (
                                  <>
                                    <div style={styles.statHeader}>Points</div>
                                    <div style={styles.statHeader}>Gross</div>
                                    <div style={styles.statHeader}>HCP</div>
                                  </>
                                )}
                              </div>
                              {eventLeaderboard.map((round, index) => {
                                const netScore = round.totalGross - round.courseHandicap;
                                return (
                                  <div key={round.id} style={styles.tableRow}>
                                    <div style={styles.position}>
                                      {getMedalEmoji(index)} {index + 1}
                                    </div>
                                    <div
                                      style={{...styles.playerName, ...(onViewProfile ? styles.playerNameLink : {})}}
                                      onClick={onViewProfile ? () => onViewProfile(round.userId) : undefined}
                                    >
                                      {getUserName(round.userId)}
                                    </div>
                                    <div style={styles.courseName}>
                                      {round.course === 'De Bleekloop' ? 'Bleekloop' : 'Plantage'}
                                    </div>
                                    {selectedEvent.type === 'finale' ? (
                                      <>
                                        <div style={styles.statValue}>
                                          <strong>{round.totalGross}</strong>
                                        </div>
                                        <div style={styles.statValue}>{round.courseHandicap}</div>
                                        <div style={styles.statValue}>
                                          <strong style={{color: '#3182ce'}}>{netScore}</strong>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div style={styles.statValue}>
                                          <strong style={{color: '#3182ce'}}>{round.totalStableford}</strong>
                                        </div>
                                        <div style={styles.statValue}>{round.totalGross}</div>
                                        <div style={styles.statValue}>{round.courseHandicap}</div>
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {view === 'finale' && (
                <div>
                  <h3 style={styles.sectionTitle}>🏆 Season Finale</h3>
                  <p style={styles.subtitle}>Bergvliet • Lowest net gross wins</p>

                  {finaleEvents.length > 1 && (
                    <div style={styles.eventSelector}>
                      <label style={styles.label}>Select Finale Event:</label>
                      <select
                        value={selectedFinaleEvent?.id || ''}
                        onChange={(e) => {
                          const event = finaleEvents.find(ev => ev.id === e.target.value);
                          setSelectedFinaleEvent(event);
                        }}
                        style={styles.select}
                      >
                        {finaleEvents.map(event => (
                          <option key={event.id} value={event.id}>
                            {event.name} - {new Date(event.date).toLocaleDateString()}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {finaleLoading ? (
                    <div style={styles.loadingState}>Loading...</div>
                  ) : finaleLeaderboard.length === 0 ? (
                    <div style={styles.emptyState}>
                      <p>No approved finale rounds yet</p>
                    </div>
                  ) : (
                    <div style={styles.leaderboardTable}>
                      <div style={{...styles.tableHeader, gridTemplateColumns: '60px 1fr 80px 60px 60px 60px'}}>
                        <div style={styles.positionHeader}>#</div>
                        <div style={styles.nameHeader}>Player</div>
                        <div style={styles.statHeader}>Teebox</div>
                        <div style={styles.statHeader}>Gross</div>
                        <div style={styles.statHeader}>Bonus</div>
                        <div style={styles.statHeader}>Net</div>
                      </div>
                      {finaleLeaderboard.map((round, index) => (
                        <div key={round.id} style={{...styles.tableRow, gridTemplateColumns: '60px 1fr 80px 60px 60px 60px'}}>
                          <div style={styles.position}>
                            {getMedalEmoji(index)} {index + 1}
                          </div>
                          <div
                            style={{...styles.playerName, ...(onViewProfile ? styles.playerNameLink : {})}}
                            onClick={onViewProfile ? () => onViewProfile(round.userId) : undefined}
                          >
                            {getUserName(round.userId)}
                          </div>
                          <div style={styles.statValue}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 10px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '700',
                              color: round.teeboxColor === 'wit' ? '#0f0f0f' : '#0f0f0f',
                              backgroundColor: TEEBOX_COLORS[round.teeboxColor] || '#6b7280',
                            }}>
                              {round.teeboxColor}
                            </span>
                          </div>
                          <div style={styles.statValue}>{round.totalGross}</div>
                          <div style={styles.statValue}>{round.finaleBonus || 0}</div>
                          <div style={styles.statValue}>
                            <strong style={{color: '#d4af37'}}>{round.finaleNetGross}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  seasonBanner: {
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    padding: '20px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    borderBottom: '1px solid #2d2d2d'
  },
  seasonName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#d4af37',
    letterSpacing: '-0.01em'
  },
  seasonDates: {
    fontSize: '14px',
    opacity: 0.9,
    color: '#9ca3af',
    fontWeight: '500'
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #2d2d2d',
    padding: '0 30px',
    gap: '10px',
    flexShrink: 0
  },
  tab: {
    padding: '15px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    transition: 'all 0.3s ease'
  },
  tabActive: {
    padding: '15px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid #d4af37',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    color: '#d4af37',
    whiteSpace: 'nowrap'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '30px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    marginTop: 0,
    marginBottom: '10px',
    letterSpacing: '-0.01em'
  },
  subtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '25px',
    fontWeight: '400'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '400'
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '400'
  },
  leaderboardTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '60px 1fr 80px 80px',
    gap: '10px',
    padding: '15px 20px',
    backgroundColor: '#0f0f0f',
    color: '#d4af37',
    fontWeight: '600',
    borderRadius: '8px 8px 0 0',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '60px 1fr 80px 80px',
    gap: '10px',
    padding: '15px 20px',
    borderBottom: '1px solid #2d2d2d',
    alignItems: 'center',
    backgroundColor: '#1e1e1e',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
  },
  positionHeader: {
    textAlign: 'center'
  },
  nameHeader: {
    textAlign: 'left'
  },
  statHeader: {
    textAlign: 'center'
  },
  position: {
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: '600',
    color: '#9ca3af'
  },
  playerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'left',
    letterSpacing: '-0.01em',
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  playerNameLink: {
    cursor: 'pointer',
    color: '#d4af37',
    textDecoration: 'underline'
  },
  courseName: {
    fontSize: '13px',
    color: '#9ca3af',
    textAlign: 'center',
    fontWeight: '500'
  },
  statValue: {
    textAlign: 'center',
    fontSize: '15px',
    color: '#d4af37',
    fontWeight: '600',
    fontVariantNumeric: 'tabular-nums'
  },
  eventSelector: {
    marginBottom: '25px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '8px',
    display: 'block'
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    cursor: 'pointer',
    fontWeight: '500'
  },
  eventInfo: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '25px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    border: '1px solid #2d2d2d'
  },
  eventDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  eventLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  eventValue: {
    fontSize: '16px',
    color: '#ffffff',
    fontWeight: '600'
  },
  changeCell: {
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px'
  }
};

export default Leaderboard;